import { Directive, Input, OnChanges, TemplateRef, ViewContainerRef } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication';
import { LogicOp, Permission } from '@app/core/authentication/permission';

/*
* DIRECTIVE
* Requires a Permission or Permission[] array and checks based on these permission(s) if the user can see the element.
* Optional parameter is a LogicOp, which you can use to specify AND, OR, NAND or NOR for the above mentioned permissions.
* If no LogicOp is provided, then the default AND is used in user.hasPermissions(permissions, logic) method.
*   -> AND: The user must have ALL of the Permissions to see the element
*   -> OR: The user must have at least ONE of the Permissions in order to see the element
*   -> NAND: The user must NOT have ALL of the Permissions in order to see the element
*   -> NOR: The user must NOT have ANY of the Permissions in order to see the element
*
* SYNTAX TO USE IN HTML.
* Ex. 1: <div *permissions="Permission.SMP_CASE_SWITCH_VIEW"></div>
* Ex. 2: <div *permissions="Permission.SMP_CASE_SWITCH_VIEW; logic: LogicOp.NAND"></div>
* Ex. 3: <div *permissions="[Permission.SMP_CASE_SWITCH_VIEW, Permission.SMP_CASE_EDIT]"></div>
* Ex. 4: <div *permissions="[Permission.SMP_CASE_SWITCH_VIEW, Permission.SMP_CASE_EDIT]; logic: LogicOp.OR"></div>
*
* NOTE:
* As this is a structural directive, it cannot be used in combination with another structural directive
* on the same element (*ngIf, *ngFor, ... )
*
*/

@Directive({
    selector: '[permissions]'
})
export class PermissionsDirective implements OnChanges {

    private _permissions: Permission | Permission[];
    private _permissionsLogic: LogicOp;
    private _accessGranted: boolean;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private authenticationService: AuthenticationService) {
    }

    public ngOnChanges() {
        this.checkAccess();
    }

    @Input()
    set permissions(permissions: Permission[]) {
        this._permissions = permissions;
    }

    @Input()
    set permissionsLogic(logic: LogicOp) {
        this._permissionsLogic = logic;
    }

    private checkAccess(): void {
        const previousAccess = this._accessGranted;
        this._accessGranted = this.authenticationService.currentUserValue
                                                        .hasPermissions(this._permissions, this._permissionsLogic);

        if (previousAccess !== this._accessGranted) {
            if (this._accessGranted) {
                this.viewContainer.createEmbeddedView(this.templateRef);
            } else {
                this.viewContainer.clear();
            }
        }
    }
}
